import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";
import { ArrowLeft, Ban, KeyRound, Loader2, Shield, ShieldOff, Trash2 } from "lucide-react";

import { AdminShell } from "@/platform/layout/admin-shell";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { useSession } from "@/platform/auth/use-session";
import {
  actOnAdminUser,
  getAdminUser,
  type AdminUserAction,
} from "@/platform/admin/api";

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<null | { action: AdminUserAction; label: string }>(null);
  const isSelf = !!session && !!id && session.user.id === id;

  const query = useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => getAdminUser(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (action: AdminUserAction) => actOnAdminUser(id!, action),
    onSuccess: (_data, action) => {
      toast.success(`${actionLabel(action)} concluído.`);
      if (action.type === "delete") {
        navigate("/admin/usuarios", { replace: true });
      } else {
        void queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
        void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      }
    },
    onError: (err) => toast.error(friendlyErrorMessage(err, "Falha na ação.")),
  });

  function performAction(action: AdminUserAction, requiresConfirm: boolean) {
    if (requiresConfirm) {
      setConfirm({ action, label: actionLabel(action) });
    } else {
      mutation.mutate(action);
    }
  }

  return (
    <AdminShell
      title="Detalhe da usuária"
      description="Perfil, assinaturas, atividade LLM recente e ações administrativas."
      actions={
        <Button asChild variant="outline" size="sm">
          <Link to="/admin/usuarios">
            <ArrowLeft className="size-3.5" />
            Voltar
          </Link>
        </Button>
      }
    >
      {query.isLoading ? (
        <Loader2 className="size-6 animate-spin text-[color:var(--color-muted-foreground)]" />
      ) : query.error || !query.data ? (
        <p className="text-[color:var(--color-danger)]">Usuária não encontrada.</p>
      ) : (
        <>
          {/* Header card */}
          <section className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-display text-[length:var(--text-h2)] font-semibold leading-tight">
                  {query.data.full_name ?? query.data.display_name ?? query.data.email}
                </h2>
                <p className="mt-1 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                  {query.data.email}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {query.data.is_admin && (
                    <Badge variant="outline" className="gap-1">
                      <Shield className="size-3" aria-hidden />
                      Admin
                    </Badge>
                  )}
                  {query.data.banned_until &&
                  new Date(query.data.banned_until) > new Date() ? (
                    <Badge variant="outline" className="text-[color:var(--color-danger)] border-[color:var(--color-danger)]">
                      Banida até {new Date(query.data.banned_until).toLocaleDateString("pt-BR")}
                    </Badge>
                  ) : null}
                  {!query.data.email_confirmed_at && (
                    <Badge variant="outline">Email não confirmado</Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {query.data.is_admin ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => performAction({ type: "set_admin", isAdmin: false }, true)}
                    disabled={mutation.isPending || isSelf}
                    title={isSelf ? "Você não pode remover admin da própria conta" : undefined}
                  >
                    <ShieldOff className="size-3.5" />
                    Remover admin
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => performAction({ type: "set_admin", isAdmin: true }, true)}
                    disabled={mutation.isPending}
                  >
                    <Shield className="size-3.5" />
                    Tornar admin
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => performAction({ type: "send_password_reset" }, false)}
                  disabled={mutation.isPending}
                >
                  <KeyRound className="size-3.5" />
                  Enviar reset de senha
                </Button>
                {query.data.banned_until &&
                new Date(query.data.banned_until) > new Date() ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => performAction({ type: "ban", days: null }, true)}
                    disabled={mutation.isPending}
                  >
                    Desbanir
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => performAction({ type: "ban", days: 30 }, true)}
                    disabled={mutation.isPending || isSelf}
                    title={isSelf ? "Você não pode banir a própria conta" : undefined}
                  >
                    <Ban className="size-3.5" />
                    Banir 30 dias
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => performAction({ type: "delete" }, true)}
                  disabled={mutation.isPending || isSelf}
                  title={isSelf ? "Você não pode deletar a própria conta" : undefined}
                  className="text-[color:var(--color-danger)] border-[color:var(--color-danger)]/40 hover:bg-[color:var(--color-danger)]/10"
                >
                  <Trash2 className="size-3.5" />
                  Deletar
                </Button>
              </div>
              {isSelf && (
                <p className="mt-3 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  Algumas ações ficam desabilitadas porque você está vendo a própria conta admin.
                </p>
              )}
            </div>

            <dl className="mt-6 grid gap-x-6 gap-y-3 sm:grid-cols-2 text-[length:var(--text-body-sm)]">
              <DefRow label="Cadastro" value={new Date(query.data.created_at).toLocaleString("pt-BR")} />
              <DefRow
                label="Último login"
                value={
                  query.data.last_sign_in_at
                    ? new Date(query.data.last_sign_in_at).toLocaleString("pt-BR")
                    : "—"
                }
              />
              <DefRow label="user_id" value={<code className="text-[length:var(--text-caption)]">{query.data.user_id}</code>} />
            </dl>
          </section>

          {/* Assinaturas */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Assinaturas
            </h3>
            {query.data.subscriptions.length === 0 ? (
              <p className="mt-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Sem assinaturas registradas.
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {query.data.subscriptions.map((s) => (
                  <li
                    key={s.id}
                    className="grid gap-3 rounded-xl border border-[--color-border] bg-[color:var(--color-paper)] p-4 text-[length:var(--text-body-sm)] sm:grid-cols-[1fr_auto] sm:items-center"
                  >
                    <div>
                      <p className="font-medium capitalize">
                        {s.tenant} · {s.plan}
                      </p>
                      <p className="mt-0.5 text-[color:var(--color-muted-foreground)]">
                        Status: <span className="capitalize">{s.status}</span>
                        {s.trial_end ? (
                          <>
                            {" · Trial até "}
                            {new Date(s.trial_end).toLocaleDateString("pt-BR")}
                          </>
                        ) : null}
                        {s.current_period_end ? (
                          <>
                            {" · Próxima cobrança "}
                            {new Date(s.current_period_end).toLocaleDateString("pt-BR")}
                          </>
                        ) : null}
                      </p>
                      {s.stripe_subscription_id ? (
                        <p className="mt-0.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                          stripe: <code>{s.stripe_subscription_id}</code>
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Perfis pedagógicos */}
          <section className="mt-6 grid gap-4 sm:grid-cols-2">
            <ProfileCard
              title="Perfil Einstein"
              data={
                query.data.einsteinProfile
                  ? [
                      ["Onboarding", query.data.einsteinProfile.onboarding_completed ? "Concluído" : "Pendente"],
                      ["Data da prova", query.data.einsteinProfile.exam_date ?? "—"],
                      ["Horas/semana", String(query.data.einsteinProfile.weekly_hours ?? "—")],
                      [
                        "Aceite dos termos",
                        query.data.einsteinProfile.consent_terms_at
                          ? new Date(query.data.einsteinProfile.consent_terms_at).toLocaleString("pt-BR")
                          : "—",
                      ],
                    ]
                  : null
              }
            />
            <ProfileCard
              title="Perfil USP"
              data={
                query.data.uspProfile
                  ? [
                      ["Onboarding", query.data.uspProfile.onboarding_completed ? "Concluído" : "Pendente"],
                      ["Curso alvo", query.data.uspProfile.curso_alvo ?? "—"],
                      ["Horas/semana", String(query.data.uspProfile.weekly_hours ?? "—")],
                    ]
                  : null
              }
            />
          </section>

          {/* LLM recente */}
          <section className="mt-6 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Últimas chamadas LLM
            </h3>
            {query.data.recentLlmCalls.length === 0 ? (
              <p className="mt-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Nenhuma chamada registrada.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-[length:var(--text-body-sm)]">
                  <thead>
                    <tr className="text-left text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                      <th className="py-2 pr-3">Quando</th>
                      <th className="py-2 pr-3">Função</th>
                      <th className="py-2 pr-3">Modelo</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3 text-right">Latência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {query.data.recentLlmCalls.map((c, i) => (
                      <tr key={i} className="border-t border-[--color-border]">
                        <td className="py-2 pr-3 text-[color:var(--color-muted-foreground)]">
                          {new Date(c.created_at).toLocaleString("pt-BR")}
                        </td>
                        <td className="py-2 pr-3">{c.function_name}</td>
                        <td className="py-2 pr-3 text-[color:var(--color-muted-foreground)]">{c.model}</td>
                        <td className="py-2 pr-3">
                          <Badge
                            variant="outline"
                            className={
                              c.status === "ok"
                                ? "text-[color:var(--color-success)] border-[color:var(--color-success)]/40"
                                : "text-[color:var(--color-danger)] border-[color:var(--color-danger)]/40"
                            }
                          >
                            {c.status}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {c.latency_ms ? `${c.latency_ms}ms` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}

      {/* Confirmação */}
      {confirm && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          onClick={() => setConfirm(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Confirmar: {confirm.label}?
            </h3>
            <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              Esta ação afeta diretamente a conta da usuária. Confirme apenas se você sabe o que está fazendo.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setConfirm(null)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  mutation.mutate(confirm.action);
                  setConfirm(null);
                }}
                loading={mutation.isPending}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function DefRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

function ProfileCard({
  title,
  data,
}: {
  title: string;
  data: Array<[string, string]> | null;
}) {
  return (
    <div className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-6">
      <h4 className="font-display text-[length:var(--text-body)] font-semibold">{title}</h4>
      {data === null ? (
        <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Nunca acessou.
        </p>
      ) : (
        <dl className="mt-3 grid gap-2 text-[length:var(--text-body-sm)]">
          {data.map(([k, v]) => (
            <div key={k} className="grid grid-cols-[auto_1fr] gap-3">
              <dt className="text-[color:var(--color-muted-foreground)]">{k}:</dt>
              <dd className="capitalize">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function actionLabel(action: AdminUserAction): string {
  switch (action.type) {
    case "set_admin":
      return action.isAdmin ? "Tornar admin" : "Remover admin";
    case "ban":
      return action.days === null ? "Desbanir" : `Banir por ${action.days} dias`;
    case "send_password_reset":
      return "Enviar reset de senha";
    case "delete":
      return "Deletar conta";
  }
}
